import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { featuresApi } from "@/lib/api/features";
import type { Feature } from "@/lib/api/types";

/**
 * The design's progress ring with time left (MissionControl: "About 1 h 5 min
 * left", "About 40 min once started", "Took 2 h").
 *
 * Time left moves with the build's progress, not a clock: each unfinished part
 * counts its usual time for its size, scaled by how long this blueprint's
 * finished parts actually took, times the share of its tasks still to do. It
 * changes when a task or part finishes.
 */

/** Typical minutes for one part of each size, before scaling by this blueprint's pace. */
const PART_MINUTES: Record<string, number> = { trivial: 10, simple: 20, moderate: 40, complex: 70 };
const DEFAULT_PART_MINUTES = 40;

export type PartTiming = Feature & {
  estimatedComplexity?: string | null;
  durationMinutes?: number | null;
  startedAt?: string | null;
  completedAt?: string | null;
};

function usual(part: PartTiming): number {
  return PART_MINUTES[part.estimatedComplexity ?? ""] ?? DEFAULT_PART_MINUTES;
}

export function isDone(part: PartTiming): boolean {
  const category = part.status?.category;
  return category === "completed" || category === "canceled" || !!part.completedAt;
}

export interface TimeEstimate {
  /** Minutes still to go. */
  minutesLeft: number;
  /** Minutes the finished parts took, for "Took …". */
  minutesTaken: number;
}

export function estimateParts(parts: PartTiming[]): TimeEstimate | null {
  if (parts.length === 0) return null;
  const finished = parts.filter((p) => isDone(p) && typeof p.durationMinutes === "number" && p.durationMinutes > 0);
  const usualFinished = finished.reduce((sum, p) => sum + usual(p), 0);
  const actualFinished = finished.reduce((sum, p) => sum + (p.durationMinutes ?? 0), 0);
  // This blueprint's pace: 1 = parts take their usual time. Kept within reason.
  const pace = usualFinished > 0 ? Math.min(4, Math.max(0.25, actualFinished / usualFinished)) : 1;

  let left = 0;
  for (const part of parts) {
    if (isDone(part)) continue;
    const tasks = part._count?.tasks ?? 0;
    const tasksDone = Math.min(tasks, part.completedTaskCount ?? 0);
    const share = tasks > 0 ? (tasks - tasksDone) / tasks : 1;
    left += usual(part) * pace * share;
  }
  return { minutesLeft: left, minutesTaken: actualFinished };
}

export function formatMinutes(minutes: number): string {
  const m = Math.max(1, Math.round(minutes));
  if (m < 60) return `${String(m)} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${String(h)} h` : `${String(h)} h ${String(rest)} min`;
}

export function ringText(
  mode: "estimating" | "not_started" | "running" | "paused" | "done",
  estimate: TimeEstimate | null
): string {
  if (mode === "done") return estimate && estimate.minutesTaken > 0 ? `Took ${formatMinutes(estimate.minutesTaken)}` : "Done";
  if (!estimate) return "Estimating…";
  const left = estimate.minutesLeft;
  if (mode === "not_started") return `About ${formatMinutes(left)} once started`;
  if (left <= 0) return "Almost done";
  return mode === "paused" ? `Paused · about ${formatMinutes(left)} left` : `About ${formatMinutes(left)} left`;
}

/**
 * The blueprint's parts with their task counts. Its own key (a plain list, not
 * useFeatures' pages). While `live`, it re-reads every 30s so progress follows
 * finished tasks.
 */
export function useBlueprintParts(epicId: string | null, live: boolean): PartTiming[] {
  const { data } = useQuery({
    queryKey: ["features", "list", { epicId, limit: 100 }, "ring"],
    queryFn: () => featuresApi.list({ epicId: epicId ?? undefined, limit: 100 }),
    enabled: !!epicId,
    refetchInterval: live ? 30_000 : false,
  });
  return useMemo(() => (data?.data ?? []) as PartTiming[], [data]);
}

export function ProgressRing({
  epicId,
  percent,
  mode,
}: {
  epicId: string | null;
  percent: number | null;
  mode: "estimating" | "not_started" | "running" | "paused" | "done";
}) {
  const parts = useBlueprintParts(epicId, mode === "running");
  const estimate = useMemo(() => estimateParts(parts), [parts]);

  const pct = Math.max(0, Math.min(100, Math.round(percent ?? 0)));
  const r = 62;
  const circumference = 2 * Math.PI * r;
  const dash = `${String((pct / 100) * circumference)} ${String(circumference)}`;
  const color = mode === "paused" ? "#F59E0B" : mode === "done" ? "#16A34A" : "#2563EB";
  const text = ringText(mode, estimate);

  return (
    <div className="flex shrink-0 flex-col items-center gap-2" data-testid="progress-ring">
      <svg width="150" height="150" viewBox="0 0 150 150" role="img" aria-label={`${String(pct)}% done. ${text}`}>
        <circle cx="75" cy="75" r={r} fill="none" className="stroke-accent" strokeWidth="12" />
        <circle
          cx="75"
          cy="75"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={dash}
          transform="rotate(-90 75 75)"
        />
        <text
          x="75"
          y="75"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="27"
          fontWeight="600"
          className="fill-foreground"
        >
          {`${String(pct)}%`}
        </text>
      </svg>
      <p aria-live="polite" className="text-center text-sm font-semibold text-foreground">
        {text}
      </p>
    </div>
  );
}
